import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSeo } from '@/hooks/useSeo';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function ContactPage() {
  useSeo({
    title: 'Contatti — Showroom Ciro Gallotti Napoli',
    description:
      'Contatta la gioielleria Ciro Gallotti a Napoli. Indirizzo: Via Chiaia 138. Tel: +39 333 645 0082. Email: cirogallotti81@libero.it. Aperti da lunedì a sabato.',
    path: '/contatti',
  });

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Messaggio inviato con successo');
    } catch (e) {
      toast.error("Errore nell'invio del messaggio");
    } finally {
      setSubmitting(false);
    }
  };

  const infoItems = [
    { icon: MapPin, title: 'Indirizzo', content: 'Via Chiaia 138\n80121 Napoli, Italia' },
    { icon: Phone, title: 'Telefono', content: '+39 333 645 0082', href: 'tel:+393336450082' },
    { icon: Mail, title: 'Email', content: 'cirogallotti81@libero.it', href: 'mailto:cirogallotti81@libero.it' },
    { icon: Clock, title: 'Orari', content: 'Lun - Sab: 10:00 - 19:00\nDomenica: Chiuso' },
  ];

  return (
    <div className="bg-ivory pt-24 md:pt-28" data-testid="contact-page">
      <header className="px-6 md:px-12 lg:px-16 py-12 md:py-20 hairline-b">
        <div className="max-w-container mx-auto">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-px bg-bordeaux" />
            <p className="text-eyebrow text-bordeaux">Contattaci</p>
          </div>
          <h1 className="font-display text-h1 text-ink">
            <span className="italic">Parliamo</span>
          </h1>
          <p className="mt-6 max-w-2xl text-ink/65 leading-relaxed">
            Per consigli, ordini personalizzati o per fissare un appuntamento: siamo a tua disposizione.
          </p>
        </div>
      </header>

      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Info & map */}
          <div className="lg:col-span-6">
            <h2 className="font-display text-h3 text-ink mb-10">Vieni a Trovarci</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-line mb-12 border border-line">
              {infoItems.map((item) => (
                <div key={item.title} className="bg-ivory p-6 flex items-start gap-4">
                  <item.icon className="w-5 h-5 text-bordeaux flex-shrink-0 mt-1" strokeWidth={1.5} />
                  <div>
                    <h3 className="text-eyebrow text-ink mb-2">{item.title}</h3>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-sm text-ink/75 hover:text-bordeaux transition-colors duration-hover whitespace-pre-line"
                      >
                        {item.content}
                      </a>
                    ) : (
                      <p className="text-sm text-ink/75 whitespace-pre-line">{item.content}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-line">
              <iframe
                title="Showroom Ciro Gallotti — Via Chiaia 138, Napoli"
                src="https://www.google.com/maps?q=Via%20Chiaia%20138%2C%20Napoli%2C%20Italia&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-6">
            <h2 className="font-display text-h3 text-ink mb-10">Inviaci un Messaggio</h2>

            {submitted ? (
              <div
                className="border border-line p-10 text-center"
                data-testid="contact-success"
              >
                <div className="w-14 h-14 border border-bordeaux rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-7 h-7 text-bordeaux" strokeWidth={1.25} />
                </div>
                <h3 className="font-display text-h3 text-ink mb-3">Messaggio Inviato</h3>
                <p className="text-muted leading-relaxed mb-8">
                  Grazie per averci contattato. Ti risponderemo al più presto.
                </p>
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ name: '', email: '', subject: '', message: '' });
                  }}
                  variant="outline"
                >
                  Invia un altro messaggio
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-eyebrow text-ink">Nome *</Label>
                    <Input id="name" name="name" value={formData.name}
                      onChange={handleChange} required className="mt-2" data-testid="contact-name" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-eyebrow text-ink">Email *</Label>
                    <Input id="email" name="email" type="email" value={formData.email}
                      onChange={handleChange} required className="mt-2" data-testid="contact-email" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-eyebrow text-ink">Oggetto *</Label>
                  <Input id="subject" name="subject" value={formData.subject}
                    onChange={handleChange} required className="mt-2" data-testid="contact-subject" />
                </div>

                <div>
                  <Label htmlFor="message" className="text-eyebrow text-ink">Messaggio *</Label>
                  <Textarea id="message" name="message" value={formData.message}
                    onChange={handleChange} required rows={6}
                    className="mt-2 resize-none" data-testid="contact-message" />
                </div>

                <Button type="submit" disabled={submitting} size="xl" variant="accent"
                  className="w-full" data-testid="contact-submit">
                  {submitting ? (
                    <span className="flex items-center gap-3">
                      <div className="w-4 h-4 border-2 border-ivory/30 border-t-ivory rounded-full animate-spin" />
                      Invio in corso…
                    </span>
                  ) : (
                    <span className="flex items-center gap-3">
                      <Send className="w-4 h-4" />
                      Invia Messaggio
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
